/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AiAgentRoutingTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/

import React from 'react';
import { ArrowRightLeft, Plus, Trash2 } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import { useI18n } from '../../../hooks/useI18n';

interface TransferRoute {
  name: string;
  destination_type: string;
  destination: string;
}

interface RoutingFormData {
  enable_hangup: boolean;
  audio_drain_ms: number;
  enable_transfer: boolean;
  open_transfer: boolean;
  transfer_routes: TransferRoute[];
}

interface AiAgentRoutingTabProps {
  formData: RoutingFormData;
  onChange: (field: keyof RoutingFormData, value: unknown) => void;
}

export function AiAgentRoutingTab({ formData, onChange }: AiAgentRoutingTabProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('aiAgents.tabs.routing') || 'Routing'} icon={<ArrowRightLeft className="h-5 w-5" />} className="mt-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
        <SimpleTooltip content={t('aiAgents.form.enableHangupTooltip') || 'Allow AI to end calls when appropriate'} position="top">
          <Label className="text-left">{t('aiAgents.form.enableHangup') || 'Enable Hang Up'}</Label>
        </SimpleTooltip>
        <Toggle value={formData.enable_hangup} onChange={(v) => onChange('enable_hangup', v)} />

        <SimpleTooltip content={t('aiAgents.form.audioDrainMsTooltip') || 'Time in ms to wait for farewell audio before transfer or hangup'} position="top">
          <Label className="text-left">{t('aiAgents.form.audioDrainMs') || 'Audio Drain (ms)'}</Label>
        </SimpleTooltip>
        <input type="number" value={formData.audio_drain_ms}
          onChange={e => onChange('audio_drain_ms', parseInt(e.target.value) || 3000)}
          className="px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
          min={1000} max={10000} step={500} />

        <SimpleTooltip content={t('aiAgents.routing.enableTransferTooltip') || 'Allow AI to transfer calls to configured routes'} position="top">
          <Label className="text-left">{t('aiAgents.routing.enableTransfer') || 'Enable Transfer'}</Label>
        </SimpleTooltip>
        <Toggle value={formData.enable_transfer} onChange={(v) => onChange('enable_transfer', v)} />

        {formData.enable_transfer ? (
          <>
            <SimpleTooltip content={t('aiAgents.routing.openTransferTooltip') || 'Allow AI to transfer calls to any extension requested by the caller.'} position="top">
              <Label className="text-left">{t('aiAgents.routing.openTransfer') || 'Open Transfer'}</Label>
            </SimpleTooltip>
            <Toggle value={formData.open_transfer} onChange={(v) => onChange('open_transfer', v)} />
          </>
        ) : (<><div /><div /></>)}
      </div>

      {formData.enable_transfer && (
        <div className="mt-6 pt-6 border-t border-border">
          <h3 className="text-sm font-medium text-foreground mb-4">{t('aiAgents.routing.transferRoutes') || 'Transfer Routes'}</h3>
          {formData.transfer_routes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('aiAgents.routing.noRoutes') || 'No transfer routes configured. Add a route to allow the AI to transfer calls.'}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-4 px-2 text-sm font-medium text-muted-foreground">
                <div className="col-span-3">{t('aiAgents.routing.routeName') || 'Route Name'}</div>
                <div className="col-span-8">{t('aiAgents.routing.destination') || 'Destination'}</div>
                <div className="col-span-1" />
              </div>
              {formData.transfer_routes.map((route, index) => (
                <div key={index} className="grid grid-cols-12 gap-4 items-center p-2 bg-muted/30 border border-border rounded-lg">
                  <div className="col-span-3">
                    <input type="text" value={route.name}
                      onChange={(e) => {
                        const routes = [...formData.transfer_routes];
                        routes[index].name = e.target.value;
                        onChange('transfer_routes', routes);
                      }}
                      className="w-full px-3 py-2 bg-background border border-input rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      placeholder="e.g., Sales, Support" />
                  </div>
                  <div className="col-span-8">
                    <ModuleDestinationSelect moduleValue={route.destination_type} destinationValue={route.destination}
                      onModuleChange={(type) => { const routes = [...formData.transfer_routes]; routes[index].destination_type = type; routes[index].destination = ''; onChange('transfer_routes', routes); }}
                      onDestinationChange={(dest) => { const routes = [...formData.transfer_routes]; routes[index].destination = dest; onChange('transfer_routes', routes); }} />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button type="button" onClick={() => onChange('transfer_routes', formData.transfer_routes.filter((_, i) => i !== index))}
                      className="p-1 hover:bg-muted rounded-md transition-colors text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-end mt-4">
            <button type="button"
              onClick={() => onChange('transfer_routes', [...formData.transfer_routes, { name: '', destination_type: '', destination: '' }])}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition-colors">
              <Plus className="h-4 w-4" />{t('common.add') || 'Add'}
            </button>
          </div>
        </div>
      )}
    </FormBox>
  );
}
